// web/landmarks.ts — Canvas 2D drawing of the named MAP LANDMARKS.
//
// Self-contained draw pass (its own module so the sim stays untouched and a render
// refactor only has to move ONE call site). Each landmark paints a colour-coded
// INFLUENCE ZONE (a tinted disc the size of its radius) plus a distinct iconic
// MARKER + name label, so the player INSTANTLY recognises important places:
//   Y Fallen Branch · @ Compost Mound · V Bird Nest · * Garden Bed · # Rock · ~ Water
//
// Drawn UNDER the live organisms/wildlife (called right after the wall pass) so a
// creature standing on a landmark still reads on top. Uses the SAME view box as the
// rest of render.ts, so it pans/zooms with the camera for free. Reads only public
// landmark accessors — no per-frame allocation beyond the tiny label strings.

import { LANDMARK } from '../src/types.ts';
import type { EcosystemWorld } from '../src/sim/ecosystemExample.ts';
import { landmarkName } from '../src/sim/landmarks.ts';

/** The shared view transform (tile → pixel): matches render.ts's ViewBox. */
interface ViewBox {
  readonly cell: number;
  readonly offX: number;
  readonly offY: number;
}

type Icon = 'branch' | 'mound' | 'nest' | 'garden' | 'rock' | 'water';

/** Per-type look, indexed by LANDMARK.*. `zone` tints the ground; `mark` is the icon. */
interface LandmarkStyle {
  readonly zone: string;  // influence-zone hue (rgb triplet for rgba() composition)
  readonly mark: string;  // icon stroke/fill colour
  readonly icon: Icon;
}
const STYLE: readonly LandmarkStyle[] = [
  { zone: '150,116,74', mark: '#c89b6a', icon: 'branch' },  // FallenBranch — dry bark
  { zone: '128,150,70', mark: '#b6cf5a', icon: 'mound' },   // CompostMound — rich olive
  { zone: '210,120,160', mark: '#ff9ec4', icon: 'nest' },   // BirdNest — aerial-danger rose
  { zone: '94,190,110', mark: '#76e88a', icon: 'garden' },  // GardenBed — verdant green
  { zone: '140,150,162', mark: '#aeb6c2', icon: 'rock' },   // RockCluster — stone grey
  { zone: '80,150,210', mark: '#7fc6f0', icon: 'water' },   // WaterPuddle — pond blue
];

/**
 * Draw every landmark for the current frame. No-op when the world has no landmark
 * system (a sibling world built without `config.landmarks`).
 */
export function drawLandmarks(
  ctx: CanvasRenderingContext2D,
  world: EcosystemWorld,
  view: ViewBox,
  fog?: { isExplored(x: number, y: number): boolean } | null,
): void {
  const lm = world.landmarks;
  if (!lm) return;
  const { cell, offX, offY } = view;
  const radius = lm.radius();
  const n = lm.count();

  for (let i = 0; i < n; i++) {
    const type = lm.typeAt(i);
    const style = STYLE[type];
    if (!style) continue;
    // Undiscovered landmarks stay hidden under the fog of war — the player only
    // knows a place once one of its units has sensed the landmark's centre tile.
    if (fog && !fog.isExplored(lm.xAt(i), lm.yAt(i))) continue;
    const cx = offX + (lm.xAt(i) + 0.5) * cell;
    const cy = offY + (lm.yAt(i) + 0.5) * cell;
    const zr = (radius + 0.5) * cell;

    // 1) INFLUENCE ZONE — a soft radial tint so the landmark's reach is visible at
    //    a glance (fertile patch / danger territory / barrier footprint).
    const grad = ctx.createRadialGradient(cx, cy, cell * 0.4, cx, cy, zr);
    grad.addColorStop(0, `rgba(${style.zone},0.20)`);
    grad.addColorStop(1, `rgba(${style.zone},0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, zr, 0, Math.PI * 2);
    ctx.fill();
    // A faint dashed boundary ring makes the territory edge legible.
    ctx.strokeStyle = `rgba(${style.zone},0.32)`;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.arc(cx, cy, zr, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    // 1b) BARRIER FOOTPRINT — the impassable Rock/Water clusters are drawn underneath as
    //     generic grey '#' walls; repaint their actual wall tiles in the landmark's themed
    //     colour (stone grey / pond blue) so a barrier reads as terrain, not abstract wall.
    //     Only true wall tiles inside the footprint are filled, each fog-gated (an
    //     undiscovered slab stays hidden). Render-only; the sim grid is untouched.
    if (type === LANDMARK.RockCluster || type === LANDMARK.WaterPuddle) {
      const grid = world.grid;
      const lx = lm.xAt(i);
      const ly = lm.yAt(i);
      ctx.fillStyle = `rgba(${style.zone},0.85)`;
      for (let ty = ly - radius; ty <= ly + radius; ty++) {
        for (let tx = lx - radius; tx <= lx + radius; tx++) {
          if (!grid.inBounds(tx, ty) || grid.isPassable(tx, ty)) continue; // walls only
          if (fog && !fog.isExplored(tx, ty)) continue;                    // hidden under fog
          ctx.fillRect(offX + tx * cell, offY + ty * cell, cell, cell);
        }
      }
    }

    // 2) ICON — a distinct silhouette so each type is identifiable without reading.
    const s = Math.max(3, cell * 0.46);
    drawIcon(ctx, style.icon, cx, cy, s, style.mark);

    // 3) LABEL — the name, when the cell is large enough to be readable.
    if (cell >= 9) {
      ctx.fillStyle = 'rgba(12,16,23,0.62)';
      const label = landmarkName(type);
      ctx.font = `${Math.max(8, Math.round(cell * 0.5))}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      const tw = ctx.measureText(label).width;
      const ly = cy + s + 2;
      ctx.fillRect(cx - tw / 2 - 3, ly - 1, tw + 6, Math.max(10, cell * 0.56));
      ctx.fillStyle = style.mark;
      ctx.fillText(label, cx, ly);
      ctx.textAlign = 'start';
      ctx.textBaseline = 'alphabetic';
    }
  }
}

/** Draw one landmark icon centred at (cx,cy), roughly fitting a 2s box. */
function drawIcon(
  ctx: CanvasRenderingContext2D,
  icon: Icon,
  cx: number,
  cy: number,
  s: number,
  color: string,
): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = Math.max(1, s * 0.22);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  switch (icon) {
    case 'branch': {
      // A forked twig (a 'Y').
      ctx.beginPath();
      ctx.moveTo(cx, cy + s);
      ctx.lineTo(cx, cy);
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx - s * 0.8, cy - s * 0.9);
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + s * 0.8, cy - s * 0.9);
      ctx.stroke();
      break;
    }
    case 'mound': {
      // A heaped dome with three specks (rotting matter).
      ctx.beginPath();
      ctx.arc(cx, cy + s * 0.4, s, Math.PI, 0);
      ctx.fill();
      ctx.fillStyle = 'rgba(40,30,18,0.7)';
      for (let k = -1; k <= 1; k++) {
        ctx.beginPath();
        ctx.arc(cx + k * s * 0.45, cy + s * 0.1, Math.max(1, s * 0.12), 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 'nest': {
      // A woven cup with two eggs.
      ctx.beginPath();
      ctx.arc(cx, cy, s, 0.15 * Math.PI, 0.85 * Math.PI, false);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx - s * 0.32, cy - s * 0.05, Math.max(1, s * 0.2), 0, Math.PI * 2);
      ctx.arc(cx + s * 0.32, cy - s * 0.05, Math.max(1, s * 0.2), 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'garden': {
      // A four-petal bloom.
      for (let k = 0; k < 4; k++) {
        const a = (k / 4) * Math.PI * 2;
        ctx.beginPath();
        ctx.ellipse(cx + Math.cos(a) * s * 0.45, cy + Math.sin(a) * s * 0.45,
          s * 0.42, s * 0.24, a, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = '#ffe07a';
      ctx.beginPath();
      ctx.arc(cx, cy, Math.max(1, s * 0.22), 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'rock': {
      // Two overlapping boulders.
      ctx.beginPath();
      ctx.moveTo(cx - s, cy + s * 0.5);
      ctx.lineTo(cx - s * 0.3, cy - s * 0.6);
      ctx.lineTo(cx + s * 0.2, cy + s * 0.2);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(cx - s * 0.1, cy + s * 0.6);
      ctx.lineTo(cx + s * 0.5, cy - s * 0.3);
      ctx.lineTo(cx + s, cy + s * 0.6);
      ctx.closePath();
      ctx.fill();
      break;
    }
    case 'water': {
      // A pool with a ripple.
      ctx.beginPath();
      ctx.ellipse(cx, cy + s * 0.2, s, s * 0.62, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.lineWidth = Math.max(1, s * 0.12);
      ctx.beginPath();
      ctx.arc(cx, cy + s * 0.1, s * 0.4, 1.05 * Math.PI, 1.95 * Math.PI);
      ctx.stroke();
      break;
    }
    default:
      break;
  }
  ctx.restore();
}

// Re-export so a caller can theme by the same enum without a second import.
export { LANDMARK };

/** Minimal positional view of the landmark layer — all first-sight detection needs.
 *  `world.landmarks` satisfies it directly (count / xAt / yAt). */
export interface LandmarkPositions {
  count(): number;
  xAt(i: number): number;
  yAt(i: number): number;
}

/**
 * Indices of landmarks whose CENTRE tile is now explored (fog-revealed) but which the
 * caller has not marked `seen` yet — i.e. freshly DISCOVERED on this check. PURE: it never
 * mutates `seen`, so the caller decides whether to NOTIFY each one or silently seed the
 * baseline (e.g. on load, to suppress a burst for already-known places). No landmark layer
 * or no fog ⇒ nothing discovered. Cheap (a handful of landmarks); safe to call per frame.
 */
export function newlyDiscoveredLandmarks(
  lm: LandmarkPositions | null | undefined,
  fog: { isExplored(x: number, y: number): boolean } | null | undefined,
  seen: ReadonlySet<number>,
): number[] {
  const out: number[] = [];
  if (!lm || !fog) return out;
  const n = lm.count();
  for (let i = 0; i < n; i++) {
    if (seen.has(i)) continue;
    if (fog.isExplored(lm.xAt(i), lm.yAt(i))) out.push(i);
  }
  return out;
}
